import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Mail, Pencil } from 'lucide-react';
import { OPEN_LEAD_STAGES, leadHistory, reopenLead, setLeadStage, winLead } from '@mkcore/crm/schema';
import { Button, Card, ErrorState, NativeSelect, PageHeader, Skeleton } from '@mkcore/ui';
import { useActionMutation, useErrorMessage } from '@/lib/mutation';
import { useCan } from '@/lib/session';
import { formatDate } from '@/lib/format';
import { Activities, ConvertDialog, EngagementFromLeadDialog, LeadDialog, LoseDialog, StageBadge, leadCompany, leadQuery, money } from '@/features/crm/leads';
import { RecordTasks } from '@/features/projects/tasks';
import { NewProposalDialog } from '@/features/proposals/proposals';
import { Meetings } from '@/features/crm/meetings';
import { MailDraftDialog } from '@/features/crm/mail-draft';
import { EngagementSelect } from '@/features/engagements/engagement-select';
import { linkLeadToEngagement } from '@mkcore/engagements/schema';
import { ReminderButton } from '@/features/collab/reminders';
import { History } from '@/components/history';
import { call } from '@/lib/api';

export const Route = createFileRoute('/_app/crm/leads/$id')({ component: LeadPage });

/** One lead (CO-0582): its details, stage and chance, the steps to win, lose, reopen or convert it, its meetings (CO-0584) and activities. */
function LeadPage() {
  const { id } = Route.useParams();
  const { t, i18n } = useTranslation();
  const message = useErrorMessage();
  const canUpdate = useCan('crm:update');
  const canCreateEngagement = useCan('engagements:create');
  const canLinkEngagement = useCan('engagements:update');
  const link = useActionMutation(linkLeadToEngagement, { invalidate: [['crm'], ['engagement']], success: t('leads.linkedEngagement') });
  const q = useQuery(leadQuery(id));
  const canPropose = useCan('proposals:create');
  const [dialog, setDialog] = useState<'edit' | 'lose' | 'convert' | 'engagement' | 'proposal' | 'mail' | null>(null);
  const stage = useActionMutation(setLeadStage, { invalidate: [['crm']] });
  const win = useActionMutation(winLead, { invalidate: [['crm']] });
  const reopen = useActionMutation(reopenLead, { invalidate: [['crm']] });
  if (q.isError) return <ErrorState title={t('errors.generic')} description={message(q.error)} />;
  if (!q.data) return <Skeleton className="h-96" />;
  const l = q.data;
  const locale = i18n.language === 'en' ? 'en' : 'nl';
  const open = (OPEN_LEAD_STAGES as readonly string[]).includes(l.stage);
  const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div className="grid grid-cols-[10rem_1fr] gap-2 text-sm">
      <dt className="text-fg-3">{label}</dt>
      <dd>{children}</dd>
    </div>
  );

  return (
    <>
      <PageHeader
        eyebrow={t('leads.title')}
        title={l.title}
        description={leadCompany(l)}
        actions={
          canUpdate && (
            <div className="flex flex-wrap items-center gap-2">
              <StageBadge stage={l.stage} />
              {open && (
                <NativeSelect aria-label={t('leads.fields.stage')} className="h-9 w-40" value={l.stage} disabled={stage.isPending} onChange={(e) => stage.mutate({ input: { id: l.id, stage: e.target.value as never } })}>
                  {OPEN_LEAD_STAGES.map((st) => (
                    <option key={st} value={st}>
                      {t(`leads.stage.${st}`)}
                    </option>
                  ))}
                </NativeSelect>
              )}
              <Button variant="secondary" onClick={() => setDialog('edit')}>
                <Pencil /> {t('common.edit')}
              </Button>
              <Button variant="secondary" onClick={() => setDialog('mail')}>
                <Mail /> {t('mailDraft.action')}
              </Button>
              <ReminderButton targetType="lead" targetId={l.id} title={l.title} path={`/crm/leads/${l.id}`} dates={[{ label: t('leads.fields.expectedCloseOn'), date: l.expectedCloseOn }]} />
              {(!l.organisationId || !l.contactId) && open && (
                <Button variant="secondary" onClick={() => setDialog('convert')}>
                  {t('leads.convert')}
                </Button>
              )}
              {open && l.organisationId && (
                <Button loading={win.isPending} onClick={() => win.mutate({ input: { id: l.id } })}>
                  {t('leads.win')}
                </Button>
              )}
              {open && (
                <Button variant="danger" onClick={() => setDialog('lose')}>
                  {t('leads.lose')}
                </Button>
              )}
              {!open && (
                <Button variant="secondary" loading={reopen.isPending} onClick={() => reopen.mutate({ input: { id: l.id } })}>
                  {t('leads.reopen')}
                </Button>
              )}
              {open && l.organisationId && canPropose && (
                <Button variant="secondary" onClick={() => setDialog('proposal')}>
                  {t('leads.makeProposal')}
                </Button>
              )}
              {l.organisationId && !l.engagementId && canCreateEngagement && (
                <Button variant="secondary" onClick={() => setDialog('engagement')}>
                  {t('leads.createEngagement')}
                </Button>
              )}
            </div>
          )
        }
      />
      <div className="grid gap-4 lg:grid-cols-[2fr_3fr]">
        <Card className="p-4">
          <h2 className="mb-3 text-lg font-bold">{t('leads.details')}</h2>
          <dl className="grid gap-2">
            <Row label={t('leads.fields.stage')}>
              <StageBadge stage={l.stage} /> {l.stage === 'lost' && l.lostReason && <span className="text-fg-2">· {l.lostReason}</span>}
            </Row>
            <Row label={t('leads.fields.value')}>{money(l.expectedValueCents, l.currency, locale)}</Row>
            <Row label={t('leads.fields.probability')}>{l.probability}%</Row>
            <Row label={t('leads.fields.weighted')}>{money(l.weightedValueCents, l.currency, locale)}</Row>
            <Row label={t('leads.fields.expectedCloseOn')}>{l.expectedCloseOn ? formatDate(l.expectedCloseOn) : '–'}</Row>
            <Row label={t('leads.fields.source')}>{l.source ?? '–'}</Row>
            <Row label={t('leads.owner')}>{l.ownerName ?? '–'}</Row>
            <Row label={t('leads.fields.organisation')}>
              {l.organisationId ? (
                <Link to="/org/$id" params={{ id: l.organisationId }} className="underline">
                  {l.organisationName}
                </Link>
              ) : (
                <span className="text-fg-2">{t('leads.prospect', { company: l.prospectCompany ?? '–', name: l.prospectName ?? '–' })}</span>
              )}
            </Row>
            <Row label={t('leads.fields.contact')}>{l.contactName ?? l.prospectEmail ?? '–'}</Row>
            {l.engagementId ? (
              <Row label={t('leads.engagement')}>
                <Link to="/engagements/$id" params={{ id: l.engagementId }} className="underline">
                  {t('leads.openEngagement')}
                </Link>
              </Row>
            ) : (
              l.organisationId &&
              canLinkEngagement && (
                // An engagement that already exists instead of a new one (#120).
                <Row label={t('leads.engagement')}>
                  <EngagementSelect organisationId={l.organisationId} value={null} disabled={link.isPending} onChange={(engagementId) => engagementId && link.mutate({ input: { id: engagementId, leadId: l.id } })} />
                </Row>
              )
            )}
            {l.lostNote && <Row label={t('leads.fields.lostNote')}>{l.lostNote}</Row>}
          </dl>
          {l.qualification && (
            <>
              <h3 className="mt-4 mb-1 text-sm font-bold">{t('leads.fields.qualification')}</h3>
              <p className="whitespace-pre-wrap text-sm text-fg-2">{l.qualification}</p>
            </>
          )}
        </Card>
        <div className="grid content-start gap-4">
          <Card className="p-4">
            <h2 className="mb-3 text-lg font-bold">{t('meetings.title')}</h2>
            <Meetings leadId={l.id} organisationId={l.organisationId} />
          </Card>
          <RecordTasks on={{ type: 'lead', id: l.id }} archived={!open} />
          <Card className="p-4">
            <h2 className="mb-3 text-lg font-bold">{t('activities.title')}</h2>
            <Activities leadId={l.id} />
          </Card>
          <Card className="p-4">
            <History queryKey={['crm', 'lead', l.id]} fetchPage={(cursor) => call(leadHistory, { id: l.id, ...(cursor && { cursor }) })} />
          </Card>
        </div>
      </div>
      {dialog === 'edit' && <LeadDialog lead={l} onClose={() => setDialog(null)} />}
      {dialog === 'lose' && <LoseDialog lead={l} onClose={() => setDialog(null)} />}
      {dialog === 'convert' && <ConvertDialog lead={l} onClose={() => setDialog(null)} />}
      {dialog === 'engagement' && <EngagementFromLeadDialog lead={l} onClose={() => setDialog(null)} />}
      {dialog === 'mail' && <MailDraftDialog leadId={l.id} defaultTo={l.organisationId ? null : l.prospectEmail} defaultSubject={l.title} onClose={() => setDialog(null)} />}
      {dialog === 'proposal' && <NewProposalDialog organisationId={l.organisationId!} leadId={l.id} title={l.title} onClose={() => setDialog(null)} />}
    </>
  );
}
